// Capacity Plan (ENG-671) — client-side types layered over the generated
// `CapacityPlanResponseDto`. The generated DTO is the wire truth; these types
// name the grid-side concepts (rows, periods, slots, pending edits).

export const RESOLUTIONS = ['day', 'week', 'month', 'quarter', 'year'] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

/** Only Day and Week are editable; Month/Quarter/Year are read-only zooms. */
export const EDITABLE_RESOLUTIONS: ReadonlySet<Resolution> = new Set(['day', 'week']);

/** Partner first day of week per the API contract: 0 = Sunday, 1 = Monday. */
export type WeekStartsOn = 0 | 1;

/** One column group of the grid — an inclusive [start, end] date bucket. */
export interface CapacityPeriod {
  /** yyyy-MM-dd, first day of the period (the stable colId/groupId prefix). */
  start: string;
  /** yyyy-MM-dd, last day of the period (inclusive). */
  end: string;
  /** Human header label, e.g. "w/c 6 Jul". */
  label: string;
}

/** Shift slot as surfaced by the capacity plan payload (factory-wide union). */
export interface CapacitySlot {
  slot_id: string;
  slot_name: string;
  display_order: number;
}

/** A grid row. The pinned totals row uses `TOTALS_ROW_ID` as its id. */
export interface CapacityRow {
  operation_id: string;
  operation_name: string;
}

/** Operation added from the toolbar this session; shown until a value lands. */
export interface SessionOperation {
  operation_id: string;
  operation_name: string;
}

/**
 * A buffered planned-shifts edit. `cell` edits target one (operation, slot,
 * day); `week` edits carry the DAILY value for a week — the server fans it
 * out onto the week's working days for the slot (calendar-driven), so the
 * displayed daily average maps 1:1 to the stepper value.
 */
export type PlannedEdit =
  | {
      kind: 'cell';
      operation_id: string;
      slot_id: string;
      plan_date: string;
      planned_shifts: number | null;
    }
  | {
      kind: 'week';
      operation_id: string;
      slot_id: string;
      week_commencing: string;
      planned_shifts: number | null;
    };

/**
 * Optimistic buffer overlay: `${operation_id}|${slot_id}|${periodStart}` →
 * the daily value the user just entered (null = cleared). Merged over the
 * query payload when deriving displayed values so edits render instantly.
 */
export type PendingOverlay = ReadonlyMap<string, number | null>;

/**
 * The view's derivation surface, threaded to every cell renderer via
 * `cellRendererParams`. Bound once per (payload, overlay, resolution) in
 * `capacity-plan-view` so renderers stay presentational and the column
 * builder stays pure.
 */
export interface CapacityCellContext {
  resolution: Resolution;
  /** True at Day/Week — steppers editable, context menu offered. */
  editable: boolean;
  getPlanned(
    operationId: string,
    slotId: string,
    period: CapacityPeriod,
  ): { value: number | null; workingDayCount: number };
  getFigures(operationId: string, period: CapacityPeriod): PeriodFigures | null;
  getTotalsPlanned(slotId: string, period: CapacityPeriod): number | null;
  getTotalsFigures(period: CapacityPeriod): PeriodFigures | null;
  onPlannedChange(
    operation: CapacityRow,
    slot: CapacitySlot,
    period: CapacityPeriod,
    value: number,
  ): void;
}

/** Daily-average figures for one operation (or the totals) over a period. */
export interface PeriodFigures {
  /** Daily-average planned Labor Shifts across all slots. */
  planned: number;
  /** Daily-average required Labor Shifts. */
  required: number;
  /** Daily-average shortfall — max(required − planned, 0). Renders on Required. */
  short: number;
  /** Daily-average over-capacity — max(planned − required, 0). Renders on Planned. */
  over: number;
  /** Total demand hours across the period (tooltip detail, not an average). */
  demandHours: number;
  /** Factory working days in the period — the averaging denominator. */
  workingDayCount: number;
}
